import { z } from 'zod'

import { pruned } from '@/lib/filters'

/** VentaHub's defaults: 90 days ahead, 60 in transit, 14 of safety stock. */
export const DEFAULT_FORECAST_DAYS = 90
export const DEFAULT_SHIPPING_DAYS = 60
export const DEFAULT_BUFFER_DAYS = 14

/** The grid's page sizes; the run is paged in the browser, after its sort. */
export const PAGE_SIZES = [25, 50, 100, 200] as const
export const DEFAULT_PAGE_SIZE = 50

export const REORDER_STATUSES = ['yes', 'loss', 'no'] as const
export type ReorderStatusKey = (typeof REORDER_STATUSES)[number]

// The days are kept as typed: a half-typed "9" or an emptied field is
// shown as it is and refused with a reason, not snapped to a number.
const days = z
  .string()
  .regex(/^-?\d{0,4}(\.\d{0,2})?$/)
  .optional()
  .catch(undefined)

/**
 * Auto ordering's URL: the warehouse and brands, the three days, whether the
 * forecast has been run for them; then the grid's search, status, sort and
 * the SKU whose breakdown is open, and the page of the grid and its size. Every field optional; defaults apply at
 * read time. The planning uploads are not here: they are one person's
 * working assumptions, dropped on refresh as VentaHub drops them.
 */
export const autoOrderingSearchSchema = z.object({
  warehouseId: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The warehouse the forecast is run for.'),
  brandIds: z
    .array(z.number().int().positive())
    .max(200)
    .optional()
    .catch(undefined)
    .describe('Brand ids the forecast is narrowed to, as a JSON array; all brands if absent.'),
  fd: days.describe('Forecast days: how far ahead demand is forecast.'),
  sd: days.describe('Shipping days: the supplier transit time.'),
  bd: days.describe('Buffer days: the safety stock, in days of demand.'),
  // Set by Run forecast: nothing loads until it is.
  go: z
    .literal(1)
    .optional()
    .catch(undefined)
    .describe('1 once the forecast has been run for these inputs; nothing loads without it.'),
  q: z
    .string()
    .max(100)
    .optional()
    .catch(undefined)
    .describe('Text matched against the SKU code, name or ASIN in the grid.'),
  status: z
    .enum(REORDER_STATUSES)
    .optional()
    .catch(undefined)
    .describe('The reorder status the grid is narrowed to.'),
  sort: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .max(64)
    .optional()
    .catch(undefined)
    .describe('The grid column id the rows are sorted by, or none.'),
  order: z.enum(['asc', 'desc']).optional().catch(undefined).describe('The sort direction.'),
  sku: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The SKU id whose forecast breakdown is open.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The page of the grid to show, from 1.'),
  size: z
    .number()
    .refine((size) => (PAGE_SIZES as readonly number[]).includes(size))
    .optional()
    .catch(undefined)
    .describe('Rows per page: 25, 50, 100 or 200.'),
})

export type AutoOrderingSearch = z.infer<typeof autoOrderingSearchSchema>

export const EMPTY_AUTO_ORDERING_SEARCH: AutoOrderingSearch = autoOrderingSearchSchema.parse({})

/** Strip defaults and empties so a clean URL stays clean. */
export function withoutAutoOrderingDefaults(search: AutoOrderingSearch): AutoOrderingSearch {
  return pruned({
    ...search,
    fd: search.fd === String(DEFAULT_FORECAST_DAYS) ? undefined : search.fd,
    sd: search.sd === String(DEFAULT_SHIPPING_DAYS) ? undefined : search.sd,
    bd: search.bd === String(DEFAULT_BUFFER_DAYS) ? undefined : search.bd,
    brandIds: search.brandIds?.length ? search.brandIds : undefined,
    page: search.page === 1 ? undefined : search.page,
    size: search.size === DEFAULT_PAGE_SIZE ? undefined : search.size,
  })
}

/** The three days as typed, the defaults where nothing is. */
export function typedDays(search: AutoOrderingSearch) {
  return {
    fd: search.fd ?? String(DEFAULT_FORECAST_DAYS),
    sd: search.sd ?? String(DEFAULT_SHIPPING_DAYS),
    bd: search.bd ?? String(DEFAULT_BUFFER_DAYS),
  }
}

export type Knobs = { forecastDays: number; shippingDays: number; bufferDays: number }

export type KnobProblem = 'forecast' | 'shipping' | 'buffer' | 'shippingOverForecast'

/**
 * The days as numbers, or what is wrong with them — VentaHub's checks, the
 * backend's own: whole numbers, Forecast and Shipping at least 1, Buffer at
 * least 0, and Shipping not over Forecast.
 */
export function readKnobs(
  typed: ReturnType<typeof typedDays>,
): { knobs: Knobs } | { problem: KnobProblem } {
  const whole = (text: string) => (/^-?\d+$/.test(text) ? Number(text) : Number.NaN)
  const forecastDays = whole(typed.fd)
  const shippingDays = whole(typed.sd)
  const bufferDays = whole(typed.bd)
  if (!(forecastDays >= 1)) return { problem: 'forecast' }
  if (!(shippingDays >= 1)) return { problem: 'shipping' }
  if (!(bufferDays >= 0)) return { problem: 'buffer' }
  if (shippingDays > forecastDays) return { problem: 'shippingOverForecast' }
  return { knobs: { forecastDays, shippingDays, bufferDays } }
}

/** Order days: FD + BD − SD, the cover the order buys. */
export function orderDaysOf(knobs: Knobs): number {
  return knobs.forecastDays + knobs.bufferDays - knobs.shippingDays
}
